
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import Head from "next/head";
import useTranslation from "next-translate/useTranslation";

import { Heading } from "@/components/Heading";
import { Grid } from "@/components/Grid";
import { BlogText } from "@/components/BlogText";

import { CTASection } from "@/sections/CTASection";
import { TestimonialsSection } from "@/sections/TestimonialsSection";
import { TutorialsBlogSection } from "@/sections/TutorialsBlogSection";
import { NewsletterSignupSection } from "@/sections/NewsletterSignupSection";
import { useTheme } from "@/hooks/useTheme";

const XDComponentsToFigma = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  return (
    <>
      <Head>
        <title>
          How to convert Figma Components to Adobe XD - Magicul Guides
        </title>
        <meta
          property="og:title"
          content="How to convert Figma Components to Adobe XD"
        />
        <meta
          property="og:url"
          content="https://magicul.io/tutorials/figma-components-to-xd"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="Learn how our converter allows you to convert files from Figma to XD while keeping your Components and Symbols."
        />
        <meta
          property="og:image"
          content="https://magicul.io/images/main-og-image.jpg"
        />
        <meta name="author" content="Magicul" />
        <meta name="publisher" content="Magicul" />
        <meta name="copyright" content="Magicul" />
        <meta
          name="keywords"
          content="xd, figma, converter, import, design, open, file converter, components"
        />
        <meta name="page-type" content="Software" />
        <link
          rel="canonical"
          href="https://magicul.io/tutorials/figma-components-to-xd"
        />
      </Head>

      <div>
        <Grid
          customStyle={{
            maxWidth: 800,
            flexDirection: "column",
            alignItems: "flex-start",
            paddingBottom: "0 !important",
          }}
        >
          <Heading as="h1">Convert Components from Figma to XD</Heading>
          <BlogText customStyle={{ marginTop: 29 }}>
            This guide will help you convert all your Components from{" "}
            <a
              href="https://magicul.io/converter/figma-to-xd"
              target="_blank"
              rel="noreferrer"
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
            >
              Figma to XD in a couple of clicks.
            </a>{" "}
            It will teach you to keep control of design resources as you move
            between tools.
          </BlogText>
          <BlogText>
            We all know that Components are vital to maintaining a design as it
            scales and it's faster to use ready-made Components in your library
            than it is to create every button from scratch.
          </BlogText>
          <Heading as="h4">
            Magicul is the only design converter that's capable of moving
            Components between Figma and Adobe XD.
          </Heading>
          <BlogText customStyle={{ marginTop: 28 }}>
            It took a year of development, but Magicul is so precise that you
            can move Components back and forth between Figma and XD with no loss
            of editability or visual data{" "}
            <a
              rel="noreferrer"
              href="https://medium.com/@sebastiantan/moving-designs-from-figma-to-xd-just-how-easy-is-it"
              target="_blank"
              style={{ color: colors.RED, fontWeight: "bold" }}
            >
              an achievement that got us featured on UX collective
            </a>
            .
          </BlogText>
          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/button-component-example.png"
            alt="conversion figma to xd"
          />
          <strong>
            <BlogText>Recommend reading 🧠🚀</BlogText>
          </strong>
          <ul>
            <li>
              <BlogText>
                <a
                  rel="noreferrer"
                  href="https://help.figma.com/hc/en-us/articles/360038662654-Guide-to-Components-in-Figma"
                  target="_blank"
                  style={{
                    color: colors.RED,
                    fontWeight: "bold",
                  }}
                >
                  Guide to Components
                </a>
                , from Figma
              </BlogText>
            </li>
            <li>
              <BlogText>
                <a
                  rel="noreferrer"
                  href="https://www.xdguru.com/adobe-xd-guide/symbols/"
                  target="_blank"
                  style={{
                    color: colors.RED,
                    fontWeight: "bold",
                  }}
                >
                  What are Components
                </a>
                , from XD Guru
              </BlogText>
            </li>
          </ul>
          <Heading as="h3">How to convert Components from Figma to XD</Heading>
          <div style={{ display: "flex" }}>
            <img
              style={{
                width: "20px",
                marginRight: "1rem",
                marginBottom: 29,
              }}
              src="/images/tutorials/figma-components-to-xd/figma.svg"
              alt="conversion figma to xd"
            />
            <BlogText>Here's a Figma design with Components</BlogText>
          </div>

          <img
            style={{
              width: "100%",
              margin: "auto",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/1-figma.gif"
            alt="figma component design demonstration"
          />

          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              style={{
                width: "20px",
                marginRight: "1rem",
                marginBottom: 29,
              }}
              src="/images/tutorials/figma-components-to-xd/xd.png"
              alt="conversion figma to xd"
            />
            <BlogText>
              Here are the same Components converted to Adobe XD
            </BlogText>
          </div>

          <img
            style={{
              margin: "auto",
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/2-xd.gif"
            alt="conversion figma to xd"
          />

          <BlogText>
            In the above I’m changing the master Components, so you can see how
            it affects all the instances of that Component - its children -
            throughout the design?
          </BlogText>

          <div
            style={{
              borderLeft: "4px solid #51B4DB",
              backgroundColor: "#F3F5F7",
              width: "100%",
              marginBottom: "28px",
            }}
          >
            <strong>
              <BlogText customStyle={{ marginLeft: 10, marginTop: 10 }}>
                Time Estimates
              </BlogText>
            </strong>
            <BlogText customStyle={{ marginLeft: 10 }}>
              ~10 seconds to convert an artboard with 500 layers. Large projects
              with 1000 + files will need up to 24 hours to finish converting.
            </BlogText>
          </div>

          <BlogText>
            For orgs that work across products and timezones, the Magicul
            converter can consolidate formats. It's the flexibility to work
            between XD, Figma and Sketch like they are all one tool.
          </BlogText>
          <BlogText>
            We have helped enterprise clients like IBM, HP and Macys migrate
            thousands of files between Figma and XD. Just look at all the nice
            things they have to say about the Magicul converter.
          </BlogText>
        </Grid>
        <TestimonialsSection />
        <Grid
          customStyle={{
            maxWidth: 800,
            flexDirection: "column",
            alignItems: "flex-start",
            paddingBottom: "0 !important",
          }}
        >
          <Heading as="h4" customStyle={{ marginBottom: 12 }}>
            Magicul understands Component hierarchy
          </Heading>
          <BlogText>
            After the conversion nested Components and still attached to their
            parents.
          </BlogText>
          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/11.png"
            alt="conversion figma to xd"
          />
          <BlogText>
            You can see that XD has both Master Components and instances of
            Master Components after the conversion.
          </BlogText>
          <BlogText>
            These buttons are part of the layout of the master profile-card
            component but are themselves instances of btn-primary.
          </BlogText>

          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/13.png"
            alt="conversion figma to xd"
          />
          <Heading as="h4" customStyle={{ marginBottom: 12 }}>
            Layout Properties are converted
          </Heading>
          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/14.gif"
            alt="conversion figma to xd"
          />

          <Heading as="h3">
            How to use Magicul to convert Figma Components to XD
          </Heading>
          <BlogText>
            <strong> Step 1:</strong> Navigate to the{" "}
            <a
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
              href="https://magicul.io/converter/figma-to-xd"
              target="_blank"
              rel="noreferrer"
            >
              Figma to XD conversion
            </a>
            .
          </BlogText>

          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/3.png"
            alt="conversion figma to xd"
          />
          <BlogText>
            <strong>Step 2:</strong> Publicly share and copy a link to your
            Figma project.
          </BlogText>
          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/4.png"
            alt="conversion figma to xd"
          />

          <BlogText>
            <strong> Step 3:</strong> Paste the public Figma link into Magicul.
          </BlogText>
          <img
            style={{
              width: "80%",
              marginBottom: 2,
            }}
            src="/images/tutorials/figma-components-to-xd/5.png"
            alt="https://www.freepik.com"
          />

          <div
            style={{
              marginTop: 15,
              marginBottom: 15,
              display: "flex",
              justifyContent: "center",
              width: "80%",
            }}
          >
            <img
              style={{
                margin: "auto",
                height: "5vh",
                width: "2vw",
              }}
              src="/images/tutorials/figma-components-to-xd/7.png"
              alt="conversion figma to xd"
            />
          </div>

          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/6.png"
            alt="conversion figma to xd"
          />

          <BlogText>
            <strong>Step 4:</strong> Pay for the file conversion.
          </BlogText>

          <BlogText>
            For more information about pricing click{" "}
            <a
              href="https://magicul.io/pricing"
              target="_blank"
              rel="noreferrer"
              style={{ color: colors.RED, fontWeight: "bold" }}
            >
              here
            </a>
            .
          </BlogText>

          <BlogText>
            <strong>Step 5:</strong> Download the file you just converted.
          </BlogText>
          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/8.png"
            alt="conversion figma to xd"
          />
          <BlogText>
            <strong>Step 6:</strong> Open the file that's saved locally in XD.
          </BlogText>
          <img
            style={{
              width: "80%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/9.png"
            alt="conversion figma to xd"
          />
          <BlogText>Here's another demo of our Components in XD</BlogText>
          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/10.gif"
            alt="conversion figma to xd"
          />

          <Heading as="h3">Help! I Can't Find The Master Components</Heading>
          <BlogText>
            When you first open the file in XD it might look a little different
            to what you expect.
          </BlogText>
          <BlogText>
            All the parent Components have been moved to the section called
            main.
          </BlogText>
          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/15.png"
            alt="conversion figma to xd"
          />
          <BlogText>
            To find your Master Components, in a hurry, you can always try
            pressing "Edit Main Component" in Adobe XD which takes you straight
            to them.
          </BlogText>
          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/figma-components-to-xd/16.png"
            alt="conversion figma to xd"
          />
        </Grid>
        <CTASection
          title="Guides like this will only get you so far. Feel the quality with your own hands. Convert your own file now!"
          href="/"
        />
        <NewsletterSignupSection />
        <TutorialsBlogSection
          id="TutorialsBlogSection"
          title={t("page/figma-components-to-xd:section/tutorialsBlog.title")}
          cards={[
            {
              content: t(
                "page/figma-components-to-xd:section/tutorialsBlog.blogs.firstBlog.text",
              ),
              media: {
                type: "IMAGE",
                title: t(
                  "page/figma-components-to-xd:section/tutorialsBlog.blogs.firstBlog.mediaTitle",
                ),
                src: "/images/blog-canva-to-photoshop.jpg",
                alt: "Convert Canva to Photoshop",
              },
              href:
                "https://magicul.io/blog/how-to-convert-canva-designs-to-photoshop",
              buttonLabel: `${t(
                "page/figma-components-to-xd:section/tutorialsBlog.buttonLabel",
              )}`,
            },
            {
              content: t(
                "page/figma-components-to-xd:section/tutorialsBlog.blogs.secondBlog.text",
              ),
              media: {
                type: "IMAGE",
                title: t(
                  "page/figma-components-to-xd:section/tutorialsBlog.blogs.secondBlog.mediaTitle",
                ),
                src:
                  "/images/tutorials/figma-components-to-xd/convert-prototypes-xd-figma-sketch.png",
                alt: "Convert XD to Figma, Sketch prototypes ",
              },
              href:
                "https://magicul.io/blog/convert-prototypes-xd-figma-sketch",
              buttonLabel: `${t(
                "page/figma-components-to-xd:section/tutorialsBlog.buttonLabel",
              )}`,
            },
            {
              content: t(
                "page/figma-components-to-xd:section/tutorialsBlog.blogs.thirdBlog.text",
              ),
              media: {
                type: "IMAGE",
                title: t(
                  "page/figma-components-to-xd:section/tutorialsBlog.blogs.thirdBlog.mediaTitle",
                ),
                src: "/images/blog-xd-to-figma.jpg",
                alt: "XD to Figma Converter",
              },
              href:
                "https://magicul.io/blog/moving-designs-from-xd-to-figma-easy-or-not",
              buttonLabel: `${t(
                "page/figma-components-to-xd:section/tutorialsBlog.buttonLabel",
              )}`,
            },
          ]}
        />
      </div>
    </>
  );
};

export default XDComponentsToFigma;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/tutorials/figma-components-to-xd',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  